import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import {
  AUTO_ENRICH_BATCH_SIZE,
  EVENT_COMMIT_QUEUE_NAMES,
  EnrichableOrder,
  PIPELINE_GATE_POLL_MS,
  PIPELINE_SAFE_DEPTH,
  PipelineGateDepths,
  RAW_IMPORT_QUEUE_NAMES,
  enrichTrackingKey,
  loadEnrichPersist,
  orderNeedsLazyPlaceEnrichment,
  saveEnrichPersist,
  sumPipelineQueueDepths,
} from '../utils/orderDetailEnrichment';

export type EnrichRefreshResult = {
  ok: boolean;
  rateLimited?: boolean;
  retryAfterMs?: number;
};

export type UsePalletwaysOrderDetailEnrichOptions = {
  orders: EnrichableOrder[];
  /** Prefer enriching these first (e.g. current table page). */
  visibleOrders?: EnrichableOrder[];
  /** When false, skip background loop and pipeline auto-resume (demo users). */
  enabled?: boolean;
  /**
   * When true, do not start the enrich loop until the user clicks
   * Fill / Continue (`startOrContinueEnrichBatch`), and never auto-continue
   * past one batch per click. Avoids unbounded auto-repair.
   */
  manualOnly?: boolean;
  refreshOrder: (
    order: EnrichableOrder,
    options?: { silent?: boolean },
  ) => Promise<EnrichRefreshResult>;
};

async function fetchPipelineGateDepths(): Promise<PipelineGateDepths | null> {
  try {
    const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.pipelineStatus());
    if (!res.ok) return null;
    const text = await res.text();
    if (text.trimStart().startsWith('<')) return null;
    const data = JSON.parse(text) as {
      queues?: Record<string, { visible?: number; notVisible?: number }>;
    };
    const queues = data.queues ?? {};
    return {
      rawImport: sumPipelineQueueDepths(queues, RAW_IMPORT_QUEUE_NAMES),
      eventCommit: sumPipelineQueueDepths(queues, EVENT_COMMIT_QUEUE_NAMES),
    };
  } catch {
    return null;
  }
}

export function usePalletwaysOrderDetailEnrich({
  orders,
  visibleOrders,
  enabled = true,
  manualOnly = false,
  refreshOrder,
}: UsePalletwaysOrderDetailEnrichOptions) {
  const persisted = useMemo(() => loadEnrichPersist(), []);
  const lazyEnrichAttemptedRef = useRef(new Set(persisted.attempted));
  const lazyEnrichPendingRef = useRef<string[]>(persisted.pending);
  const lazyEnrichUsedRef = useRef(persisted.used);
  const lazyEnrichBudgetRef = useRef(persisted.budget);
  const lazyEnrichInFlightRef = useRef(new Set<string>());
  const ordersForEnrichRef = useRef<EnrichableOrder[]>([]);
  const paginatedForEnrichRef = useRef<EnrichableOrder[]>([]);
  const refreshOrderRef = useRef(refreshOrder);
  refreshOrderRef.current = refreshOrder;

  const [bgEnrichPending, setBgEnrichPending] = useState(0);
  const [bgEnrichPaused, setBgEnrichPaused] = useState(
    () => persisted.used >= persisted.budget && persisted.pending.length > 0,
  );
  /** Explicit user pause — skips pipeline auto-resume until Fill/Continue. */
  const userPausedRef = useRef(false);
  const [userPaused, setUserPaused] = useState(false);
  /** Bumped to (re)start the enrich loop after pause / user click. */
  const [enrichKick, setEnrichKick] = useState(0);
  const [pipelineGateDepths, setPipelineGateDepths] = useState<PipelineGateDepths | null>(null);

  useEffect(() => {
    ordersForEnrichRef.current = orders;
    paginatedForEnrichRef.current = visibleOrders ?? orders;
  }, [orders, visibleOrders]);

  // Background enrich loop — resumes from sessionStorage after HMR / remount
  useEffect(() => {
    if (!enabled) return;
    // Wait for an explicit Fill / Continue click before any network work.
    if (manualOnly && enrichKick === 0) return;

    if (userPausedRef.current) {
      setBgEnrichPaused(true);
      return;
    }

    if (lazyEnrichUsedRef.current >= lazyEnrichBudgetRef.current) {
      setBgEnrichPaused(true);
      const pending = ordersForEnrichRef.current
        .filter((o) => {
          const key = enrichTrackingKey(o);
          return orderNeedsLazyPlaceEnrichment(o) && key && !lazyEnrichAttemptedRef.current.has(key);
        })
        .map(enrichTrackingKey)
        .filter(Boolean);
      lazyEnrichPendingRef.current = pending;
      setBgEnrichPending(0);
      return;
    }

    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const persistQueue = (candidateKeys: string[]) => {
      lazyEnrichPendingRef.current = candidateKeys;
      saveEnrichPersist(
        lazyEnrichAttemptedRef.current,
        candidateKeys,
        lazyEnrichUsedRef.current,
        lazyEnrichBudgetRef.current,
      );
    };

    const tick = async () => {
      if (cancelled || userPausedRef.current) return;
      const allOrders = ordersForEnrichRef.current;
      const visible = paginatedForEnrichRef.current;
      const attempted = lazyEnrichAttemptedRef.current;
      const inFlight = lazyEnrichInFlightRef.current;
      const used = lazyEnrichUsedRef.current;
      const budget = lazyEnrichBudgetRef.current;
      const savedPending = lazyEnrichPendingRef.current;

      if (used >= budget) {
        setBgEnrichPaused(true);
        setBgEnrichPending(0);
        persistQueue(
          allOrders
            .filter((o) => orderNeedsLazyPlaceEnrichment(o) && !attempted.has(enrichTrackingKey(o)))
            .map(enrichTrackingKey)
            .filter(Boolean),
        );
        return;
      }

      setBgEnrichPaused(false);

      const incomplete = allOrders.filter((o) => {
        const key = enrichTrackingKey(o);
        return (
          orderNeedsLazyPlaceEnrichment(o)
          && key
          && !attempted.has(key)
          && !inFlight.has(key)
        );
      });

      if (incomplete.length === 0) {
        setBgEnrichPending(0);
        persistQueue([]);
        return;
      }

      const byTracking = new Map<string, EnrichableOrder>();
      for (const o of incomplete) {
        const key = enrichTrackingKey(o);
        if (!byTracking.has(key)) byTracking.set(key, o);
      }
      const candidateKeys = Array.from(byTracking.keys());
      setBgEnrichPending(candidateKeys.length);

      const visibleKeys = new Set(visible.map(enrichTrackingKey).filter(Boolean));
      const priorPending = savedPending.filter((id) => byTracking.has(id));
      const priorSet = new Set(priorPending);
      const orderedKeys: string[] = [];
      const pushed = new Set<string>();
      const pushUnique = (id: string) => {
        if (!byTracking.has(id) || pushed.has(id)) return;
        pushed.add(id);
        orderedKeys.push(id);
      };
      for (const id of candidateKeys) {
        if (visibleKeys.has(id)) pushUnique(id);
      }
      for (const id of priorPending) pushUnique(id);
      for (const id of candidateKeys) {
        if (!visibleKeys.has(id) && !priorSet.has(id)) pushUnique(id);
      }

      const nextKey = orderedKeys[0];
      if (!nextKey) return;
      const next = byTracking.get(nextKey);
      if (!next) return;

      persistQueue(orderedKeys);

      inFlight.add(nextKey);
      const result = await refreshOrderRef.current(next, { silent: true });
      inFlight.delete(nextKey);

      if (cancelled) return;

      if (result.rateLimited) {
        const backoffMs = result.retryAfterMs || 5000;
        timeout = setTimeout(tick, backoffMs);
        return;
      }

      attempted.add(nextKey);
      lazyEnrichUsedRef.current = used + 1;
      const remaining = orderedKeys.filter((id) => id !== nextKey);
      persistQueue(remaining);

      timeout = setTimeout(tick, remaining.length > 0 ? 1000 : 2500);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
    };
  }, [enabled, enrichKick, userPaused, manualOnly]);

  /** Still blank (including IDs already tried this session). */
  const blankDetailsCount = useMemo(() => {
    return orders.filter((o) => orderNeedsLazyPlaceEnrichment(o) && enrichTrackingKey(o)).length;
  }, [orders, bgEnrichPending, bgEnrichPaused, enrichKick]);

  /** Blank and not yet attempted — drives auto-resume / first-pass queue. */
  const missingDetailsCount = useMemo(() => {
    const attempted = lazyEnrichAttemptedRef.current;
    return orders.filter((o) => {
      const key = enrichTrackingKey(o);
      return orderNeedsLazyPlaceEnrichment(o) && key && !attempted.has(key);
    }).length;
  }, [orders, bgEnrichPending, bgEnrichPaused, enrichKick]);

  const startOrContinueEnrichBatch = useCallback((options?: { retryIncomplete?: boolean }) => {
    if (options?.retryIncomplete) {
      const attempted = lazyEnrichAttemptedRef.current;
      for (const o of ordersForEnrichRef.current) {
        const key = enrichTrackingKey(o);
        if (key && orderNeedsLazyPlaceEnrichment(o) && attempted.has(key)) {
          attempted.delete(key);
        }
      }
    }

    if (lazyEnrichUsedRef.current >= lazyEnrichBudgetRef.current) {
      lazyEnrichBudgetRef.current += AUTO_ENRICH_BATCH_SIZE;
    }
    saveEnrichPersist(
      lazyEnrichAttemptedRef.current,
      lazyEnrichPendingRef.current,
      lazyEnrichUsedRef.current,
      lazyEnrichBudgetRef.current,
    );
    userPausedRef.current = false;
    setUserPaused(false);
    setBgEnrichPaused(false);
    setPipelineGateDepths(null);
    setEnrichKick((k) => k + 1);
  }, []);

  const pauseEnrichBatch = useCallback(() => {
    if (userPausedRef.current) return;
    userPausedRef.current = true;
    setUserPaused(true);
    setBgEnrichPaused(true);
    const pending = ordersForEnrichRef.current
      .filter((o) => {
        const key = enrichTrackingKey(o);
        return orderNeedsLazyPlaceEnrichment(o) && key && !lazyEnrichAttemptedRef.current.has(key);
      })
      .map(enrichTrackingKey)
      .filter(Boolean);
    lazyEnrichPendingRef.current = pending;
    setBgEnrichPending(pending.length);
    saveEnrichPersist(
      lazyEnrichAttemptedRef.current,
      pending,
      lazyEnrichUsedRef.current,
      lazyEnrichBudgetRef.current,
    );
  }, []);

  // When a batch pauses, wait until Raw Import + Event Commit are quiet, then continue.
  useEffect(() => {
    if (!enabled || !bgEnrichPaused || userPaused) return;
    // manualOnly: one click = one batch. Never auto-continue — each further
    // batch needs another Fill / Continue click, so repair requests stay
    // bounded by operator clicks (no durable cross-session dedup exists yet).
    if (manualOnly) return;
    if (missingDetailsCount <= 0) return;

    let cancelled = false;
    const poll = async () => {
      const depths = await fetchPipelineGateDepths();
      if (cancelled) return;
      if (depths === null) return;
      setPipelineGateDepths(depths);
      if (
        depths.rawImport <= PIPELINE_SAFE_DEPTH
        && depths.eventCommit <= PIPELINE_SAFE_DEPTH
      ) {
        startOrContinueEnrichBatch();
      }
    };

    void poll();
    const id = setInterval(() => { void poll(); }, PIPELINE_GATE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [
    bgEnrichPaused,
    userPaused,
    missingDetailsCount,
    enabled,
    manualOnly,
    startOrContinueEnrichBatch,
  ]);

  return {
    blankDetailsCount,
    missingDetailsCount,
    bgEnrichPending,
    bgEnrichPaused,
    userPaused,
    pipelineGateDepths,
    enrichUsed: lazyEnrichUsedRef.current,
    enrichBudget: lazyEnrichBudgetRef.current,
    startOrContinueEnrichBatch,
    pauseEnrichBatch,
    PIPELINE_SAFE_DEPTH,
  };
}
